import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

export function AdminHeader({ title, subtitle, action }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-white">{title}</h1>
        {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function ImageUpload({ value, onChange, label = "Image", accept = "image/*" }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.file_id);
      toast.success("Uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div>
      <p className="text-sm text-slate-300 mb-1.5">{label}</p>
      {value ? (
        <div className="space-y-2">
          <div className="relative inline-block">
            <img src={fileUrl(value)} alt="preview" className="h-28 w-auto rounded-lg border border-white/10 object-cover" />
            <button type="button" onClick={() => onChange(null)} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center" data-testid="remove-image"><X className="h-3.5 w-3.5" /></button>
          </div>
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="block text-xs text-brand-cyan hover:underline disabled:opacity-60" data-testid="replace-image">
            {uploading ? "Uploading..." : "Replace image"}
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
          className="flex flex-col items-center justify-center gap-2 h-28 w-full rounded-lg border-2 border-dashed border-white/15 text-slate-500 hover:border-brand-cyan/50 hover:text-brand-cyan transition-colors" data-testid="image-upload-btn">
          {uploading ? <Loader2 className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
          <span className="text-xs">{uploading ? "Uploading..." : "Click to upload"}</span>
        </button>
      )}
      <input ref={ref} type="file" accept={accept} onChange={handle} className="hidden" />
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-500/10 text-amber-400", approved: "bg-brand-green/10 text-brand-green",
    rejected: "bg-red-500/10 text-red-400", active: "bg-brand-green/10 text-brand-green",
    inactive: "bg-slate-500/10 text-slate-400", open: "bg-brand-cyan/10 text-brand-cyan",
    closed: "bg-slate-500/10 text-slate-400",
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium capitalize ${map[status] || "bg-slate-500/10 text-slate-400"}`}>{status}</span>;
}
